// src/pages/PropertyDocuments.jsx
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTY_DOCUMENTS, UPLOAD_PROPERTY_DOCUMENT, DELETE_PROPERTY_DOCUMENT } from '../graphql/properties';

const PropertyDocuments = () => {
  const { id } = useParams();
  const [uploadingFile, setUploadingFile] = useState(null);
  
  const { loading, error, data, refetch } = useQuery(GET_PROPERTY_DOCUMENTS, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const [uploadDocument] = useMutation(UPLOAD_PROPERTY_DOCUMENT, {
    onCompleted: () => {
      setUploadingFile(null);
      refetch();
    },
    onError: (error) => {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
      setUploadingFile(null);
    }
  });

  const [deleteDocument] = useMutation(DELETE_PROPERTY_DOCUMENT, {
    onCompleted: () => refetch(),
    onError: (error) => {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  });

  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      setUploadingFile(type);
      uploadDocument({
        variables: {
          propertyId: id,
          file: file,
          type: type
        }
      });
    }
  };

  const handleDeleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteDocument({ variables: { id: documentId } });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getDocumentsByType = (type) => {
    return data?.property?.documents?.filter(doc => doc.type === type) || [];
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { property } = data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Property Documents
        </h1>
        <div className="flex space-x-3">
          <Link to={`/properties/${id}`} className="flex items-center text-blue-600 hover:text-blue-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Property Details
          </Link>
        </div>
      </div>

      {/* Property Info */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex items-center">
          <div className="bg-blue-100 text-blue-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
            {property.referenceNumber?.substring(0, 2) || 'PR'}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{property.referenceNumber || 'Property'}</h3>
            <p className="mt-1 text-sm text-gray-500">{property.address?.street}</p>
            <p className="text-sm text-gray-500">{property.address?.suburb}, {property.address?.city} {property.address?.postalCode}</p>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="space-y-6">
        {/* Lease Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Lease Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Rental agreements and lease-related documents</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'lease' ? 'Uploading...' : 'Upload Lease'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'lease')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'lease'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('lease').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('lease').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No lease documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Inspection Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Inspection Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Property inspection reports and certificates</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'inspection' ? 'Uploading...' : 'Upload Inspection'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'inspection')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'inspection'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('inspection').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('inspection').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No inspection documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Maintenance Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Maintenance Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Maintenance records and repair documentation</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'maintenance' ? 'Uploading...' : 'Upload Maintenance'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'maintenance')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'maintenance'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('maintenance').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('maintenance').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No maintenance documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Other Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Other Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Additional property-related documents</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'other' ? 'Uploading...' : 'Upload Document'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'other')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'other'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('other').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('other').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No other documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDocuments;